import React from 'react';
import { ArrowLeft, Lock, Eye, Database, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100">
      {/* 顶部导航 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            返回
          </Button>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-600" />
            <h1 className="text-xl font-semibold text-gray-900">隐私保护详情</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid gap-6">
          {/* 隐私保护概览 */}
          <Card className="shadow-lg border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                您的隐私，我们的承诺
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Eye className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900">透明公开</h3>
                  <p className="text-sm text-gray-600 mt-1">清楚告知数据使用方式</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Lock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900">安全加密</h3>
                  <p className="text-sm text-gray-600 mt-1">采用行业标准加密技术</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Database className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900">最小收集</h3>
                  <p className="text-sm text-gray-600 mt-1">只收集必要的信息</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 数据收集详情 */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">我们收集哪些信息？</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">必需</Badge>
                  <div>
                    <h4 className="font-medium text-gray-900">基本账户信息</h4>
                    <p className="text-sm text-gray-600">用户名、邮箱地址、学校信息 - 用于创建和管理您的账户</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <Badge variant="secondary" className="bg-green-100 text-green-800">可选</Badge>
                  <div>
                    <h4 className="font-medium text-gray-900">个人资料信息</h4>
                    <p className="text-sm text-gray-600">头像、昵称、个人简介、年级专业 - 用于个性化您的资料</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">自动</Badge>
                  <div>
                    <h4 className="font-medium text-gray-900">使用数据</h4>
                    <p className="text-sm text-gray-600">访问日志、设备信息、使用偏好 - 用于改善服务质量</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800">内容</Badge>
                  <div>
                    <h4 className="font-medium text-gray-900">用户生成内容</h4>
                    <p className="text-sm text-gray-600">帖子、评论、图片、互动记录 - 用于提供社交功能</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 数据使用方式 */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">我们如何使用您的信息？</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    核心服务功能
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• 账户创建和身份验证</li>
                    <li>• 内容发布和展示</li>
                    <li>• 社交互动功能</li>
                    <li>• 消息通知服务</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    服务优化
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• 个性化内容推荐</li>
                    <li>• 用户体验改进</li>
                    <li>• 系统性能优化</li>
                    <li>• 安全防护加强</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 隐私控制 */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">您的隐私控制权</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">匿名发帖</h4>
                    <p className="text-sm text-gray-600">可以选择匿名发布内容，保护身份隐私</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">已启用</Badge>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">资料可见性</h4>
                    <p className="text-sm text-gray-600">控制个人资料对其他用户的可见程度</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">可设置</Badge>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">数据导出</h4>
                    <p className="text-sm text-gray-600">随时导出您的个人数据和发布内容</p>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">支持</Badge>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">账户删除</h4>
                    <p className="text-sm text-gray-600">可以随时删除账户和相关数据</p>
                  </div>
                  <Badge className="bg-red-100 text-red-800">支持</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 安全措施 */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">我们的安全措施</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">技术安全</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      HTTPS加密传输
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      数据库加密存储
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      定期安全审计
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      访问权限控制
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">管理安全</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      员工隐私培训
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      最小权限原则
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      数据访问记录
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      应急响应机制
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 联系方式 */}
          <Card className="shadow-lg border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle className="text-lg text-gray-900">隐私问题咨询</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                如果您对我们的隐私政策有任何疑问，或需要行使您的隐私权利，请随时联系我们：
              </p>
              <div className="bg-white border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900">隐私保护专员：</span>
                  <span className="text-gray-600">privacy@dawu-wall.com</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900">客服热线：</span>
                  <span className="text-gray-600">400-123-4567（工作日 9:00-18:00）</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900">响应时间：</span>
                  <span className="text-gray-600">我们将在3个工作日内回复您的咨询</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 底部操作区 */}
          <div className="flex justify-center gap-4 pt-6">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="px-8"
            >
              返回
            </Button>
            <Button
              onClick={() => {
                // 这里可以添加同意逻辑
                navigate('/register');
              }}
              className="px-8 bg-purple-600 hover:bg-purple-700"
            >
              我已阅读并同意
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;